import type { AppState } from '../types';
import { getSession, supabase, cloudConfigured } from './supabase';
import { isTelegram } from './telegram';
import { defaultState } from './storage';
import { applyCalendarSnapshot } from './calendar';
import { cacheLicenseReceipt, clearCachedLicenseReceipt, getLicenseDeviceContext } from './license-device';

const SESSION_KEY = 'ef-telegram-session';

function functionUrl(name: string) {
  const base = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  return base ? `${base}/functions/v1/${name}` : '';
}

async function publishableHeaders(): Promise<Record<string, string>> {
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  return key ? { apikey: key } : {};
}

function mergeHeaders(...sources: Array<HeadersInit | undefined>): Headers {
  const merged = new Headers();
  for (const source of sources) {
    if (!source) continue;
    new Headers(source).forEach((value, key) => merged.set(key, value));
  }
  return merged;
}

export async function ensureTelegramSession(force = false) {
  if (!cloudConfigured || !isTelegram()) return null;
  const existing = localStorage.getItem(SESSION_KEY);
  if (existing && !force) return existing;
  if (force) localStorage.removeItem(SESSION_KEY);
  const res = await fetch(functionUrl('telegram-auth'), {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...(await publishableHeaders()) },
    body: JSON.stringify({ initData: window.Telegram?.WebApp?.initData }),
  });
  if (!res.ok) throw new Error('Telegram sessiyasini tasdiqlab bo‘lmadi.');
  const data = await res.json();
  localStorage.setItem(SESSION_KEY, data.sessionToken);
  return data.sessionToken as string;
}

async function authHeaders() {
  const session = await getSession();
  const telegramSession = localStorage.getItem(SESSION_KEY) || await ensureTelegramSession();
  return {
    ...(await publishableHeaders()),
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    ...(telegramSession ? { 'x-ef-session': telegramSession } : {}),
    'content-type': 'application/json',
  };
}

export async function edgeFetch(path: string, init: RequestInit = {}, requireAuth = true) {
  if (!cloudConfigured) return new Response(JSON.stringify({ error: 'Cloud backend configured emas.' }), { status: 503, headers: { 'content-type': 'application/json' } });
  const name = path.replace(/^\//, '');
  const send = async () => {
    const headers = requireAuth ? await authHeaders() : { ...(await publishableHeaders()), 'content-type': 'application/json' };
    return fetch(functionUrl(name), { ...init, headers: mergeHeaders(headers, init.headers) });
  };
  let response = await send();
  if (requireAuth && response.status === 401 && isTelegram()) {
    localStorage.removeItem(SESSION_KEY);
    await ensureTelegramSession(true);
    response = await send();
  }
  return response;
}

export async function pullCloudState(): Promise<AppState | null> {
  if (!cloudConfigured) return null;

  try {
    const res = await edgeFetch('/sync-state', { method: 'GET' }, true);
    if (!res.ok) return null;

    const data = await res.json();
    // A fresh cloud account may not have a user_state row yet. In that case start from
    // defaults, never from unrelated stale browser data.
    const cloudState: AppState = data.state
      ? (data.state as AppState)
      : defaultState();

    const profile = data.profile ?? {};
    const canonical = data.canonicalProgress ?? {};
    const analytics = data.analyticsSummary ?? {};
    cloudState.profile = {
      ...cloudState.profile,
      name: data.displayName || cloudState.profile.name || '',
      email: data.email || cloudState.profile.email,
      goal: profile.goal || cloudState.profile.goal,
      level: profile.level || cloudState.profile.level,
      studyTime: profile.studyTime || cloudState.profile.studyTime,
      dailyMinutes: Number(profile.dailyMinutes ?? cloudState.profile.dailyMinutes ?? 20),
      timezone: profile.timezone || cloudState.profile.timezone || 'Asia/Samarkand',
      onboardingComplete: Boolean(data.onboardingComplete),
      onboardingCompletedAt: data.onboardingCompletedAt || undefined,
      courseStartedOn: data.courseStartedOn || undefined,
      trialStartedAt: data.trialStartedAt || undefined,
      // Paid access is derived only from the authoritative License Hub entitlement.
      proUntil: data.entitlement?.ends_at || undefined,
    };

    cloudState.progress = {
      ...cloudState.progress,
      day: Number(data.courseDay ?? canonical.current_day ?? cloudState.progress.day ?? 1),
      courseDate: data.courseDate || cloudState.progress.courseDate,
      todayMissionCompleted: Boolean(data.todayMissionCompleted),
      dailyCompletionDates: Array.isArray(data.dailyCompletionDates) ? data.dailyCompletionDates.map(String) : [],
      completedDays: Array.isArray(data.completedDays) ? data.completedDays.map(Number).filter(Number.isFinite) : [],
      missedDays: Number(data.missedDays ?? canonical.missed_days ?? 0),
      courseFinished: Boolean(data.courseFinished),
      xp: Number(canonical.xp ?? cloudState.progress.xp ?? 0),
      streak: Number(data.streak ?? canonical.streak ?? 0),
      rank: String(canonical.rank_code ?? cloudState.progress.rank ?? 'Starter'),
      reviewDebt: Number(canonical.review_debt ?? cloudState.progress.reviewDebt ?? 0),
      lastStudyDate: canonical.last_completion_date || cloudState.progress.lastStudyDate,
      totalAnswers: Number(analytics.totalAnswers ?? cloudState.progress.totalAnswers ?? 0),
      correctAnswers: Number(analytics.correctAnswers ?? cloudState.progress.correctAnswers ?? 0),
      lessonSessions: Array.isArray(analytics.lessonSessions)
        ? analytics.lessonSessions.map((session: Record<string, unknown>) => ({
            day: Number(session.day ?? 1),
            courseDate: session.courseDate ? String(session.courseDate) : undefined,
            completedAt: String(session.completedAt ?? new Date().toISOString()),
            score: Number(session.score ?? 0),
            passed: Boolean(session.passed),
            reviewDebtAfter: Number(session.reviewDebtAfter ?? 0),
          }))
        : cloudState.progress.lessonSessions,
    };

    // Local calculation is only a resilient display fallback. Since courseStartedOn/timezone
    // came from the server, this cannot advance the course by finishing a lesson.
    return applyCalendarSnapshot(cloudState);
  } catch {
    return null;
  }
}

export async function pushCloudState(state: AppState) {
  if (!cloudConfigured) return false;
  try {
    const res = await edgeFetch('/sync-state', { method: 'POST', body: JSON.stringify({ state }) }, true);
    return res.ok;
  } catch { return false; }
}

export async function cloudAuthStatus() {
  const session = await getSession();
  return { configured: cloudConfigured, signedIn: Boolean(session), email: session?.user.email ?? null, supabase };
}

export async function adminRequest(action: string, payload: Record<string, unknown> = {}) {
  if (!cloudConfigured) throw new Error('Admin cloud backend sozlanmagan.');
  const session = await getSession();
  if (!session?.access_token) throw new Error('Admin cloud account bilan kirish kerak.');
  const res = await fetch(functionUrl('admin-api'), {
    method: 'POST',
    headers: { ...(await publishableHeaders()), Authorization: `Bearer ${session.access_token}`, 'content-type': 'application/json' },
    body: JSON.stringify({ action, ...payload }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Admin API xatosi.');
  return data;
}

export async function createAccountLinkCode() {
  if (!cloudConfigured) throw new Error('Account linking uchun production cloud backend sozlanishi shart.');
  const res = await edgeFetch('/account-link', { method: 'POST', body: JSON.stringify({ action: 'create' }) }, true);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Link code yaratilmadi.');
  return data;
}

export async function redeemAccountLinkCode(code: string) {
  if (!cloudConfigured) throw new Error('Account linking uchun production cloud backend sozlanishi shart.');
  const res = await edgeFetch('/account-link', { method: 'POST', body: JSON.stringify({ action: 'redeem', code }) }, true);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Account link xatosi.');
  return data;
}


export interface LicenseHubPlan {
  id: number;
  product_id: number;
  code: string;
  name: string;
  duration_days: number;
  price_uzs: number;
  device_limit: number;
  grace_days: number;
  status: string;
  features?: string[];
}

export interface LicenseHubProduct {
  id: number;
  slug: string;
  name: string;
  description?: string;
  platform?: string;
  client_id?: string;
  support_url?: string;
  status?: string;
}

export interface LicenseHubStatus {
  ok: boolean;
  configured: boolean;
  active: boolean;
  reason: string;
  product?: string;
  user_id?: string;
  account_ref?: string;
  plan_code?: string;
  plan_name?: string;
  expires_at?: string;
  device_limit?: number;
  offline_grace_until?: string;
  license_receipt?: string;
  publicSigningKey?: string;
}


export interface LicenseHubDiagnostic {
  ok: boolean;
  configured: boolean;
  diagnostic: boolean;
  accountFingerprint: string;
  hubReachable: boolean;
  mismatchLikely: boolean;
  reconciled: boolean;
  hub: {
    active: boolean;
    reason: string;
    planCode?: string | null;
    planName?: string | null;
    expiresAt?: string | null;
    sysoneUserHint?: string | null;
  };
  local: {
    mirror: null | { active: boolean; reason: string; planCode?: string | null; planName?: string | null; expiresAt?: string | null; sysoneUserHint?: string | null; checkedAt?: string | null };
    entitlement: null | { status: string; endsAt?: string | null; externalRefHint?: string | null; updatedAt?: string | null };
  };
}

export async function getLicenseHubProduct() {
  const res = await edgeFetch('/license-hub?action=product', { method: 'GET' }, false);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'SysOne License Hub product ma’lumotlari olinmadi.');
  return data as { ok: boolean; configured: boolean; hubUrl: string; publicSigningKey?: string; product: LicenseHubProduct; plans: LicenseHubPlan[] };
}

export async function getLicenseHubCheckout() {
  const res = await edgeFetch('/license-hub', { method: 'POST', body: JSON.stringify({ action: 'checkout' }) }, true);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'SysOne License Hub checkout ochilmadi.');
  return data as { ok: boolean; hubUrl: string; checkoutUrl: string; product: LicenseHubProduct; plans: LicenseHubPlan[] };
}

async function callLicenseHub(action: 'status' | 'redeem', licenseKey = ''): Promise<LicenseHubStatus> {
  const device = await getLicenseDeviceContext();
  const res = await edgeFetch('/license-hub', {
    method: 'POST',
    body: JSON.stringify({
      action,
      licenseKey,
      deviceId: device.deviceId,
      deviceName: device.deviceName,
      platform: device.platform,
      appVersion: device.appVersion,
    }),
  }, true);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'SysOne License Hub so‘rovi bajarilmadi.');
  const status = data as LicenseHubStatus;
  if (status.active && status.license_receipt && status.publicSigningKey && status.expires_at && status.offline_grace_until && status.product) {
    await cacheLicenseReceipt({
      receipt: status.license_receipt,
      publicKeyB64Url: status.publicSigningKey,
      product: status.product,
      expiresAt: status.expires_at,
      offlineGraceUntil: status.offline_grace_until,
    });
  } else if (!status.active && !['LICENSE_HUB_UNAVAILABLE'].includes(status.reason)) {
    await clearCachedLicenseReceipt();
  }
  return status;
}

export async function refreshLicenseHubStatus() {
  return callLicenseHub('status');
}

export async function redeemLicenseHubKey(licenseKey: string) {
  return callLicenseHub('redeem', licenseKey.trim().toUpperCase());
}


async function licenseDiagnosticAction(action: 'diagnose' | 'reconcile'): Promise<LicenseHubDiagnostic> {
  const device = await getLicenseDeviceContext();
  const res = await edgeFetch('/license-hub', {
    method: 'POST',
    body: JSON.stringify({
      action,
      deviceId: device.deviceId,
      deviceName: device.deviceName,
      platform: device.platform,
      appVersion: device.appVersion,
    }),
  }, true);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'License Hub diagnostikasi bajarilmadi.');
  return data as LicenseHubDiagnostic;
}

export async function diagnoseLicenseHubAccount() {
  return licenseDiagnosticAction('diagnose');
}

export async function reconcileLicenseHubAccount() {
  return licenseDiagnosticAction('reconcile');
}

export async function exportAccountData() {
  const res = await edgeFetch('/account-data', { method: 'POST', body: JSON.stringify({ action: 'export' }) }, true);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Account eksport qilinmadi.');
  return data;
}

export async function requestAccountDeletion(reason = '') {
  const res = await edgeFetch('/account-data', { method: 'POST', body: JSON.stringify({ action: 'request_delete', reason }) }, true);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Delete request yaratilmadi.');
  return data;
}
